import { Card, Group, Stack, Text, ThemeIcon, Tooltip } from '@/components/ui';
import { IconArrowDownRight, IconArrowUpRight, IconMinus } from '@tabler/icons-react';
import type { MetricDelta } from '../utils/analyticsHelpers';
import { formatPercent } from '../utils/analyticsHelpers';

interface Props {
  label: string;
  value: string;
  delta?: MetricDelta;
  icon?: React.ReactNode;
  color?: string;
  /** Explains what the metric means, or why it is unavailable. */
  hint?: string;
  onClick?: () => void;
}

/**
 * A KPI tile with an optional period-over-period delta.
 *
 * "Higher is better" is not universal: for unfollows or cost-per-conversion an increase
 * is bad. `invertDelta` is deliberately NOT a prop -- callers that need it pass a delta
 * whose direction has already been interpreted, so the colour logic lives in one place
 * rather than being re-decided per tile.
 */
export function MetricDeltaCard({ label, value, delta, icon, color = 'blue', hint, onClick }: Props) {
  const hasDelta = delta?.direction !== undefined && delta.changePercent !== undefined;

  const deltaColor =
    delta?.direction === 'up' ? 'teal' : delta?.direction === 'down' ? 'red' : 'dimmed';

  const DeltaIcon =
    delta?.direction === 'up' ? IconArrowUpRight
    : delta?.direction === 'down' ? IconArrowDownRight
    : IconMinus;

  const card = (
    <Card
      shadow="sm"
      padding="md"
      radius="md"
      withBorder
      onClick={onClick}
      style={onClick ? { cursor: 'pointer' } : undefined}
    >
      <Stack gap={6}>
        <Group justify="space-between" wrap="nowrap">
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} lineClamp={1}>
            {label}
          </Text>
          {icon && (
            <ThemeIcon size="sm" variant="light" color={color}>
              {icon}
            </ThemeIcon>
          )}
        </Group>

        <Text size="xl" fw={700} lh={1.1}>
          {value}
        </Text>

        {hasDelta ? (
          <Group gap={4} wrap="nowrap">
            <DeltaIcon size={14} color={`var(--mantine-color-${deltaColor}-6)`} />
            <Text size="xs" c={deltaColor} fw={600}>
              {formatPercent(Math.abs(delta!.changePercent!))}
            </Text>
            <Text size="xs" c="dimmed">
              vs previous period
            </Text>
          </Group>
        ) : (
          // Reserve the row so tiles with and without a delta stay the same height.
          <Text size="xs" c="dimmed">
            {delta?.previous === undefined ? 'No comparison' : 'No change'}
          </Text>
        )}
      </Stack>
    </Card>
  );

  return hint ? <Tooltip label={hint} multiline w={280}>{card}</Tooltip> : card;
}
